import { createSelector } from 'reselect';
import { parse } from 'qs';
import { utils } from '@saucelabs/sl-module-performance';
import { access as rootCauseAccess } from '@saucelabs/sl-module-root-cause';
import { records as userRecords } from '@saucelabs/sl-module-user';

import { filterCommands } from './utils';

const job = (store) => store.apps.job;
const performance = (store) => store.apps.performance;
const location = (store) => store.router.location;

const containerSelector = createSelector([
  job,
  (store) => store.user || new userRecords.UserRecord(), // fallback to anonymous user for embeded pages with auth
  (store) => store.experiments,
],
(filteredState, user) => ({
  loading: filteredState.container.get('loading'),
  notifications: filteredState.container.get('notifications'),

  commands: filteredState.job.get('commands'),
  job: filteredState.job.get('job'),
  user,
}));

const WebdriverContainerSelector = createSelector([
  (store) => store.experiments,
  job,
  (store) => store.user || new userRecords.UserRecord(), // fallback to anonymous user for embeded pages with auth
  location,
  performance,
],
(experiments, filteredState, user, routerLocation, performanceStore) => ({
  auth: parse(routerLocation.search, { ignoreQueryPrefix: true }).auth,
  columnsRatio: filteredState.container.get('ratio'),
  loading: filteredState.container.get('loading'),
  player: filteredState.container.get('player'),

  filteredCommands: filterCommands({
    automationBackend: filteredState.job.get('job').automation_backend,
    commands: filteredState.job.get('commands'),
    filterState: filteredState.container.get('commandsFilter'),
    performanceData: performanceStore.jobPerformance.get('rawPerformanceData'),
    performanceRegressions: performanceStore.regression.get('performanceRegressions'),
    rootCauseLines: filteredState.rootCause.get('lines'),
  }),
  assets: filteredState.job.get('assets'),
  commandsLoadStatus: filteredState.job.get('commandsLoadStatus'),
  job: filteredState.job.get('job'),
  selectorsMapping: filteredState.job.get('selectorsMapping'),
  build: filteredState.job.get('build'),
  isVideoReady: filteredState.job.get('assetListFetched'),

  files: filteredState.files.get('files'),
  selectedFilename: filteredState.files.get('selectedFilename'),
  logFetchStatus: filteredState.files.get('status'),
  user,
  isOpenSauceUser: !user.isAuthenticated(),
  isJobOwner: user.username === filteredState.job.getIn(['job', 'owner']),
  regressionData: performanceStore.regression.get('performanceRegressions'),
  regressionOverview: utils.getRegressionOverview(performanceStore.regression.get('performanceRegressions')),
  loaderId: performanceStore.jobPerformance.get('loaderId'),
  routerSearchQuery: routerLocation.search,
  failureAnalysisEnabled: rootCauseAccess.isFailureAnalysisEnabled(user),
  hasRootCause: filteredState.container.get('commandsFilter').hasRootCause,
  annotationsName: filteredState.rootCause.get('annotationsName'),
  jobHash: filteredState.rootCause.get('testHash'),
  pattern: filteredState.rootCause.get('pattern'),
  showFailedTests: filteredState.container.get('commandsFilter').showFailedTests,
  scrollTimeStamp: filteredState.job.get('scrollTimeStamp'),
}));

const WebdriverLivePreviewContainerSelector = createSelector(
  job,
  (store) => store.user || new userRecords.UserRecord(), // fallback to anonymous user for embeded pages with auth
  (filteredState, user) => ({
    job: filteredState.job.get('job'),
    build: filteredState.job.get('build'),
    isFreeUser: user.isFreeUser(),
    isOpenSauceUser: !user.isAuthenticated(),
  }),
);

const PublicRestrictedContainerSelector = createSelector(
  job,
  (store) => store.user || new userRecords.UserRecord(), // fallback to anonymous user for embeded pages with auth
  (filteredState, user) => ({
    build: filteredState.job.get('build'),
    columnsRatio: filteredState.container.get('ratio'),
    job: filteredState.job.get('job'),
    isOpenSauceUser: !user.isAuthenticated(),
    player: filteredState.container.get('player'),
  }),
);

const espressoContainerSelector = WebdriverContainerSelector;

const headlessContainerSelector = createSelector([
  WebdriverContainerSelector,
  job,
],
(webdriverSelectorState, filteredState) => ({
  ...webdriverSelectorState,
  galleryFullScreen: filteredState.container.get('galleryFullScreen'),
}));

const playerSelector = (state) => job(state).container.get('player');

const allCommandsSelector = createSelector(job, (jobState) => jobState.job.get('commands'));

// Respects commandsFilter
const filteredCommandsSelector = createSelector(job, performance, (
  jobState, performanceStore,
) => filterCommands({
  commands: jobState.job.get('commands'),
  filterState: jobState.container.get('commandsFilter'),
  performanceData: performanceStore.jobPerformance.get('rawPerformanceData'),
  performanceRegressions: performanceStore.regression.get('performanceRegressions'),
}));

// Respects commandsFilter
const commandSelector = (id) => (state) => filteredCommandsSelector(state)
  .find((command) => command.id === id);

const appiumContainerSelector = WebdriverContainerSelector;

const appiumLivePreviewContainerSelector = createSelector(
  job,
  (filteredState) => ({
    job: filteredState.job.get('job'),
    build: filteredState.job.get('build'),
    device: filteredState.rdcPreviewSession.get('device'),
    sessionState: filteredState.rdcPreviewSession.get('sessionState'),
  }),
);

const rawJobContainerSelector = createSelector(
  job,
  (filteredState) => ({ rawJob: filteredState.rawJob }),
);

const videoPlayerJobDataSelector = createSelector([job], (jobStore) => ({
  startTime: jobStore.job.getIn(['job', 'start_time']) * 1000,
}));

const networkRequestParamSelector = createSelector([
  filteredCommandsSelector,
  job,
  (store, options) => options,
],
(filteredCommands, jobStore, options = {}) => {
  const timeDiff = new Date(options.startedDateTime).getTime() - jobStore.job.getIn(['job', 'start_time']) * 1000;
  return {
    commands: filteredCommands,
    currentTime: timeDiff / 1000,
    player: jobStore.container.get('player'),
  };
});

export default containerSelector;
export {
  WebdriverContainerSelector,
  WebdriverLivePreviewContainerSelector,
  PublicRestrictedContainerSelector,
  allCommandsSelector,
  appiumContainerSelector,
  appiumLivePreviewContainerSelector,
  commandSelector,
  containerSelector,
  espressoContainerSelector,
  filteredCommandsSelector,
  headlessContainerSelector,
  job,
  playerSelector,
  rawJobContainerSelector,
  location,
  videoPlayerJobDataSelector,
  networkRequestParamSelector,
};
